// TODO: maybe we should move this file to a different lib since it's not a type. Or maybe we could rename the "type" lib to something else, like "commmon-lib". We want to make sure that this lib can be consumed by both frontend and backend code.

import { TriggerType } from "@prisma/client";
import { PersonalizationValue, TriggerStepOpenModalConfig } from "./types";
import {
  TriggerCreate,
  TriggerForEditor,
  TriggerStepCreate,
  TriggerStepType,
} from "./TriggerCreate";
import invariant from "ts-invariant";

type VariableType = VariableTypeInfo | "boolean" | "string" | "number";

export type VariableTypeInfo = { [key: string]: VariableType };
export type TaskField = { [key: string]: PossibleValuesByField };

export type PossibleValuesByField = { value: string; label: string };

export type VariablesInfo =
  | { [key in TriggerStepType]: VariableTypeInfo }
  | { [key in TriggerStepType]: TaskField };

const tasks: VariablesInfo = {
  create_channel: {},
  delete_channel: {},
  test_captcha: {},
  send_http_request: {},
  send_message: {},
  send_survey: {},
  send_multiple_select: {},
  open_modal: {},
  idle: {},
  cancel_tasks: {},
  change_nickname: {},
  delete_message: {},
  edit_channel_category: {},
  edit_roles: {},
  edit_dots_tags: {},
  kick_member: {},
  send_direct_message: {},
  send_webhook: {},
  verify_email: {},
  create_thread: {},
  get_gif: {},
  find_user: {},
  save_user_field: {},
};

function isUserTriggered(triggerType: TriggerType) {
  return [
    "team_join",
    "channel_join",
    "command",
    "role_change",
    "message_create",
    "button_click",
    "receive_webhook",
  ].includes(triggerType);
}

function triggerTypeSpecificVariables(
  trigger: TriggerCreate
): VariableTypeInfo {
  let result = {};
  if (isUserTriggered(trigger.type)) {
    result = { ...result, user: { id: "string", username: "string" } };
  }
  switch (trigger.type) {
    case "channel_join":
      result = { ...result, channelId: "string" };
      break;
    case "message_create":
      result = { ...result, channelId: "string" };
      break;
    case "command":
      if (trigger.configs?.options?.length) {
        trigger.configs?.options.forEach((option) => {
          result = {
            ...result,
            [`command.options.${option.name}`]: option.type,
          };
        });
      }
      if (trigger.configs?.options?.length) {
        trigger.configs?.options.map((option) => {
          result = {
            ...result,
            [`command.options.${option.name}`]: option.type,
          };
        });
      }
      break;
    default:
      break;
  }
  return result;
}

function getEntryStep(data: TriggerCreate) {
  // By default, first step is always the entry step
  return data.steps.find((s) => s.isEntry) ?? data.steps[0];
}

function collectPreviousSteps(trigger: TriggerCreate, stepId: string) {
  const result: TriggerStepCreate[] = [];
  const stepsMap = new Map();
  trigger.steps.forEach((s) => stepsMap.set(s.id, s));
  const visited = new Set();

  function dfs(step: TriggerStepCreate, path: TriggerStepCreate[]) {
    if (!step) return;
    if (visited.has(step.id)) return;
    visited.add(step.id);
    if (step.id === stepId) {
      result.push(...path);
    }
    const neighbors = step.transitions.map((t) => stepsMap.get(t.targetStepId));
    if (step.if?.elseStepId) {
      const elseStep = stepsMap.get(step.if.elseStepId);
      if (elseStep) {
        neighbors.push(elseStep);
      }
    }
    for (const n of neighbors) {
      dfs(n, [...path, n]);
    }
  }
  const entryStep = getEntryStep(trigger);
  dfs(entryStep, [entryStep]);
  return result;
}

function variablesByStepType(stepType: TriggerStepType) {
  const vars: { [key in TriggerStepType]: VariableTypeInfo } = {
    ...(tasks as { [key in TriggerStepType]: VariableTypeInfo }),
    create_channel: { channelId: "string", channelName: "string" },
    delete_channel: { channelId: "string", deleted: "boolean" },
    test_captcha: {
      validText: "string",
      messageId: "string",
    },
    send_http_request: {
      type: "string",
      status: "string",
      content: "string",
      httpResponse: { content: "string" },
    },
    send_message: {
      messageId: "string",
      //   response: { content: "string" },
      userResponse: { content: "string" },
    },
    send_survey: {
      messageId: "string",
      //   response: { content: "string" },
      userResponse: { content: "string" },
    },
    send_multiple_select: {
      messageId: "string",
      //   response: { content: "string" },
      userResponse: { content: "string" },
    },
    open_modal: {
      messageId: "string",
    },
    idle: {
      //   response: { content: "string" },
      userResponse: { content: "string" },
    },
    find_user: { userId: "string" },
    save_user_field: {},
  };
  return vars[stepType];
}

export function getPossibleValuesByStepProperty(
  stepType: TriggerStepType,
  property: string
): PossibleValuesByField[] {
  const customUserFields: PossibleValuesByField[] = [
    {
      value: "email",
      label: "Email",
    },
    {
      value: "location",
      label: "Location",
    },
    {
      value: "name",
      label: "Name",
    },
    {
      value: "referral_source",
      label: "Referral Source",
    },
    {
      value: "wallet_address",
      label: "Wallet Address",
    },
    {
      value: "twitter",
      label: "Twitter",
    },
  ];

  const vars = {
    ...(tasks as { [key in TriggerStepType]: TaskField }),
    find_user: { field: customUserFields },
    save_user_field: { field: customUserFields },
  };

  //TODO: Fix this hack
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  //@ts-ignore
  return vars[stepType][property];
}

function isTriggerForEditor(trigger: any): trigger is TriggerForEditor {
  return (
    trigger.isDraft === false ||
    (trigger.isDraft === true && trigger.liveTriggerConfigs)
  );
}

function toTriggerCreate(trigger: TriggerForEditor) {
  return (
    trigger.isDraft
      ? {
          ...trigger,
          ...trigger.liveTriggerConfigs, // Use live trigger configs to get global vars
          ...(trigger.type === "command" ? { configs: trigger.configs } : null), //If it's command trigger, use the configs from the draft trigger because same configurations will be same for both live and draft triggers
        }
      : trigger
  ) as TriggerCreate;
}

/**
 * Return global variables that are available in the trigger by type.
 * @param trigger
 */
export function getAvailableGlobalVariables(
  trigger: TriggerCreate | TriggerForEditor
) {
  const triggerCreate = isTriggerForEditor(trigger)
    ? toTriggerCreate(trigger)
    : trigger;
  const globalCommon: VariableTypeInfo = {
    platform: "string",
    communityId: "string",
  };
  return {
    ...globalCommon,
    ...triggerTypeSpecificVariables(triggerCreate),
  };
}

export function getAvailableVariablesForStep(
  trigger: TriggerCreate | TriggerForEditor,
  stepId: string
) {
  const triggerCreate = isTriggerForEditor(trigger)
    ? toTriggerCreate(trigger)
    : trigger;

  if (!triggerCreate.steps.some((s) => s.id === stepId))
    throw new Error(`Invalid stepId: ${stepId}`);

  const previousSteps = collectPreviousSteps(triggerCreate, stepId);
  const variablesFromPreviousSteps = previousSteps.reduce(
    (result, step) => ({
      ...result,
      [step.id]: {
        ...variablesByStepType(step.type),
        $$stepName: step.name || step.type.replace("_", " "),
      },
    }),
    {}
  );

  const result: VariableTypeInfo = {
    ...getAvailableGlobalVariables(triggerCreate),
    ...variablesFromPreviousSteps,
  };

  return result;
}

export function getAvailableChannelVariablesChannelsBySteps(
  trigger: TriggerCreate | TriggerForEditor
) {
  let triggerConfigChannelVariables: {
    key: string;
    id: string;
    name: string;
  }[] = [];
  if (trigger.type === "button_click") {
    triggerConfigChannelVariables = [
      {
        key: `{{interaction.channelId}}`,
        id: `{{interaction.channelId}}`,
        name: `button_click channel`,
      },
    ];
  }
  const stepChannelVariables = trigger.steps
    .filter((s) => s.type === "create_channel")
    .map((s) => ({
      key: `{{${s.id}.channelId}}`,
      id: `{{${s.id}.channelId}}`,
      name: `${s.name ? s.name : s.id}.channel`,
    }));
  return triggerConfigChannelVariables.concat(stepChannelVariables);
}

export function getAllParentSteps(
  trigger: TriggerCreate | TriggerForEditor,
  step: TriggerStepCreate
) {
  return trigger.steps.filter((s) =>
    s.transitions.some((t) => t.targetStepId === step.id)
  );
}

/**
 * Return all variables that are available in the trigger by type.
 * @param trigger
 */
export function getGlobalPersonalizationVariables(
  trigger: TriggerCreate | TriggerForEditor
) {
  const availableVariables = getAvailableGlobalVariables(trigger);
  return getPersonalizationValues(availableVariables, "");
}

/**
 * Return all variables that are available in the step.
 * @param trigger
 * @param step
 */
export function getPersonalizationVariablesByStep(
  trigger: TriggerCreate | TriggerForEditor,
  step: TriggerStepCreate
) {
  const availableVariables = getAvailableVariablesForStep(trigger, step.id);

  //getAvailableVariablesForStep returns variables of the selected step as well so
  //we need to remove them from the list
  const availableVariablesFiltered = Object.keys(availableVariables)
    .filter((stepId) => stepId !== step.id)
    .reduce((acc, stepId) => {
      return { ...acc, [stepId]: availableVariables[stepId] };
    }, {} as Record<string, any>);

  cleanModalVariables(trigger, availableVariablesFiltered);

  return getPersonalizationValues(availableVariablesFiltered, "");
}

export function getMembersVariables(insertVariables: PersonalizationValue[]) {
  const findUserVariables = insertVariables.filter((insertVariable) =>
    insertVariable.value.includes("userId")
  );
  const members = [
    { id: "{{user.id}}", name: "{{User in the flow}}" },
    ...findUserVariables.map((userVariable) => ({
      id: `{{${userVariable.id}}}`,
      name: userVariable.userText || userVariable.value,
    })),
  ];
  return members;
}

function cleanModalVariables(
  trigger: TriggerCreate | TriggerForEditor,
  variables: Record<string, any>
) {
  const variablesStepId = Object.keys(variables);
  // Find all steps that are modals
  // We check if the step is in variables keys because it possible that the step
  // selected is before some modal step, so we don't want to include variables of steps
  // that are after the selected step
  const modalSteps = trigger.steps.filter(
    (s) => s.type === "open_modal" && variablesStepId.includes(s.id)
  );

  /**
   *  Get all fields id of all modals in order to set the path of the variable. This is neeeded because
   *  the variable path is used to the user when selecting a modal variable
   */
  for (const modalStep of modalSteps) {
    invariant(modalStep.configs);
    const modalConfig = modalStep.configs as TriggerStepOpenModalConfig;
    variables[modalStep.id].response = modalConfig.fields
      .map((field) => field.id)
      .reduce(
        (acc, fieldId) => {
          acc.modalData[fieldId] = "string";
          return acc;
        },
        {
          modalData: {},
        } as { modalData: { [key: string]: "string" } }
      );
  }
}

/**
 * Returns a list of all the steps that are before the given step in the trigger.
 * availableVariables is an object with the variables available for each step
 * in the case the step has a variable that is an object, we need to iterate over its properties
 * for example for a  send_message step, the availableVariables object will look like this:
 * sendMessage.userResponse.content (the userResponse is an object) and we need to iterate over its properties.
 * This method also returns two types of values the value to be showed in the editor that has as
 * prefix the stepName and the value to be used in the backend side that has as prefix the stepId.
 * @param availableVariables
 * @param path
 * @param rootName
 */
export function getPersonalizationValues(
  availableVariables: Record<string, any>,
  path: string,
  rootName?: string
): PersonalizationValue[] {
  const rootNamePath = rootName || availableVariables["$$stepName"];
  const existRootStepName = availableVariables["$$stepName"] || rootNamePath;
  let pathWithNameStep = "";

  if (existRootStepName) {
    const paths = path.split(".");
    paths[0] = availableVariables["$$stepName"] || rootNamePath;
    pathWithNameStep = paths.join(".");
  }

  const variables = Object.keys(availableVariables)
    .filter((key) => key !== "$$stepName")
    .map((key) => {
      const value = availableVariables[key];
      if (typeof value === "object") {
        return getPersonalizationValues(
          availableVariables[key],
          `${path}${key}.`,
          rootNamePath
        );
      }

      return mentionVariables(
        key,
        existRootStepName ? `${pathWithNameStep}${key}` : `${path}${key}`,
        `${path}${key}`
      );
    });

  function deepFlatten<T>(arr: T[]): T[] {
    return arr.reduce(
      (acc, val) =>
        Array.isArray(val) ? acc.concat(deepFlatten(val)) : acc.concat(val),
      [] as T[]
    );
  }

  return deepFlatten(variables as unknown as PersonalizationValue[]);
}

/**
 * Return variables that can be used in the editor where:
 *
 * id: is the value to be stored in the backend and the first part of the path is the step id in the case
 * of step variables as opposed to global variables.
 *
 * value: a value to be used in <DiscordTextEditor /> to format the html properly maybe
 * we should find a way to remove this property in the future.
 *
 * userText: is the value to be showed in the editor and the first part of the path is the step name
 *
 * @param variableName
 * @param userText
 * @param path
 */
function mentionVariables(
  variableName: string,
  userText: string,
  path: string
): PersonalizationValue[] {
  //Variables that are not going to be displayed in the personalization list
  const excludePaths = ["user.id"];

  if (variableName === "username") {
    return [
      {
        id: "user.username",
        value: "username}}",
        userText: "{{username}}",
      },
      {
        id: "<@user.id>",
        value: "@username}}",
        userText: "{{@username}}",
      },
    ];
  }

  if (path.includes("modalData")) {
    const splitUserText = userText.split(".");

    //Show to the user the name of the step and the name of the field
    const userTextUpdated = `${splitUserText[0]}.${
      splitUserText[splitUserText.length - 1]
    }`;

    return [
      {
        id: path,
        userText: `{{${userTextUpdated}}}`,
        value: `${userTextUpdated}}}`,
      },
    ];
  }

  if (excludePaths.includes(path)) {
    return [];
  }

  return [
    {
      id: path,
      value: `${userText}}}`,
      userText: `{{${userText}}}`,
    },
  ];
}
